import { settingDirection } from "../helpers/teamReassessment";
import TeamWeightSetup from "./TeamWeightSetup";
import TeamIndividualEditor from "./TeamIndividualEditor";
import TeamValueChanges from "./TeamValueChanges";
import { settingChanges, goalFieldLabel } from "../helpers/teamSettingValues";
import { changedFields } from "../helpers/teamAnalysis";
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  proposalEvidence,
  normalizeWeights,
  effectiveWeights,
  moveWeight,
  weightedProposal,
  metricFor,
} from "../helpers/teamProposal";
import {
  futureSummary,
  editResult,
  historySummary,
} from "../helpers/teamAnalysis";
import { homeAdvantage, strengthRanking } from "../helpers/teamSettingsImpact";
import { TeamMatches } from "./TeamPerformance";

export default function TeamProposalWorkbench({
  data,
  teamId,
  model,
  question,
  focusField = null,
  onFocusField,
  recent,
  en,
  mode,
  steps,
  draft,
  ranking,
  busy: parentBusy,
  onApplyResult,
  onApplyStat,
  onPreviewStat,
  stat,
}) {
  const workbenchRef = useRef(null);
  const previewGeneration = useRef(0);
  const t = (d, e) => (en ? e : d),
    fmt = (n) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", {
            useGrouping: false,
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          })
        : "—";
  const evidence = useMemo(
    () => proposalEvidence(data, teamId, model, question, recent, focusField),
    [data, teamId, model, question, recent, focusField]
  );
  const [weights, setWeights] = useState({}),
    [adapt, setAdapt] = useState(25),
    [selectedSources, setSelectedSources] = useState([]),
    [manualExact, setManualExact] = useState(null),
    [requested, setRequested] = useState(false),
    [manual, setManual] = useState(""),
    [manualOverride, setManualOverride] = useState(false),
    [info, setInfo] = useState(null),
    [notice, setNotice] = useState(""),
    [effect, setEffect] = useState(null),
    [pending, setPending] = useState(false),
    [tour, setTour] = useState(-1),
    [tourMode, setTourMode] = useState("explain"),
    [playing, setPlaying] = useState(false);
  const busy = parentBusy || (tour >= 0 && tourMode === "explain");
  useEffect(() => {
    setRequested(false);
    setManualOverride(false);
    setEffect(null);
  }, [data.result_revision, data.state?.revision]);
  useEffect(() => {
    if (tour >= 0)
      workbenchRef.current
        ?.querySelector(".tw-highlight")
        ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [tour]);
  const current = futureSummary(
    model === "result"
      ? draft?.[teamId]
      : ranking?.find((r) => r.team_id === Number(teamId))
  );
  const field =
    focusField ||
    (question === "strength"
      ? "total_strength"
      : question === "goals"
      ? "total_goals"
      : question === "draws"
      ? "draw_factor"
      : "for_homefactor");
  const supported = ["strength", "goals", "home"].includes(question);
  const saved =
    question === "draws"
      ? model === "result"
        ? data.result_teams?.[teamId]?.draw_factor
        : data.result_parameters?.drawfactor
      : evidence.currentSaved;
  const draftValue =
    question === "home" ? homeAdvantage(current) : current?.[field];
  const keys = evidence.sources
    .filter((s) => s.enabled || s.desiredSelectable)
    .map((s) => s.key);
  const effective = effectiveWeights(evidence, weights);
  const normalized = normalizeWeights(weights, keys),
    configured = keys.some((k) => normalized[k] > 0);
  const proposal =
    requested && supported
      ? weightedProposal(evidence, weights, adapt / 100)
      : null;
  const target = manualOverride
    ? manual.trim()
      ? manualExact ?? Number(manual.replace(",", "."))
      : null
    : proposal?.target;
  const sourceNames = {
    should: t("Gemessene Saisonleistung", "Measured season performance"),
    real: t("Tatsächliche Ergebnisse", "Actual results"),
    market: t("Markt", "Market"),
    recent: t(
      `Gemessene Leistung · letzte ${recent}`,
      `Measured performance · last ${recent}`
    ),
    other:
      model === "result"
        ? t("Statistikmodell · gespeichert", "Statistics model · saved")
        : t("Ergebnismodell · gespeichert", "Results model · saved"),
    previous: t("Vorsaison", "Previous season"),
    external: "xGoals · " + t("externer Anbieter", "external provider"),
  };
  const globalProfileKey = `betty.team-analysis.personal-weights.v2.${model}`;
  const teamProfileKey = `betty.team-analysis.team-weights.v1.${teamId}.${model}.${question}${
    focusField ? "." + focusField : ""
  }`;
  const profileKey = `betty.team-analysis.weights.v1.${model}.${question}${
    focusField ? "." + focusField : ""
  }`;
  useEffect(() => {
    setWeights({});
    setSelectedSources([]);
    setManualExact(null);
    setRequested(false);
    setManualOverride(false);
    setEffect(null);
    setNotice("");
    setTour(-1);
    workbenchRef.current
      ?.querySelector(".tw-tour-controls")
      ?.scrollIntoView?.({ block: "center" });
    setPlaying(false);
    try {
      const p = JSON.parse(
        localStorage.getItem(teamProfileKey) ||
          localStorage.getItem(globalProfileKey) ||
          localStorage.getItem(profileKey)
      );
      if (p?.weights) {
        setWeights(p.weights);
        setSelectedSources(
          p.selectedSources ||
            Object.keys(p.weights).filter((k) => p.weights[k] > 0)
        );
        setAdapt(p.adapt ?? 25);
      }
    } catch {}
  }, [profileKey, teamProfileKey, globalProfileKey]);
  useEffect(() => {
    previewGeneration.current++;
    setEffect(null);
  }, [
    target,
    model,
    question,
    focusField,
    current?.total_strength,
    current?.total_goals,
    current?.for_homefactor,
    current?.against_homefactor,
    current?.draw_factor,
  ]);
  const displayed = steps
    .filter((s) => s.key !== "recent")
    .map((s) => {
      if (!focusField) return s;
      const metric = (source) =>
        metricFor(evidence.games, teamId, source, question, focusField);
      const source = s.key;
      const savedModel =
        source === "saved-result"
          ? data.result_teams?.[teamId]
          : source === "saved-statistics"
          ? data.ranking?.find((r) => r.team_id === Number(teamId))
          : null;
      const previous = evidence.sources.find((r) => r.key === "previous");
      const summary = historySummary(evidence.games, teamId, source);
      const isSaved = source.startsWith("saved-");
      return {
        ...s,
        comparisons: [],
        baselineDifferences: [],
        unit: t("Tore pro Spiel", "Goals per match"),
        value: isSaved
          ? futureSummary(savedModel)?.[focusField]
          : source === "previous"
          ? previous.full
          : metric(source),
        coverage: isSaved
          ? t("Gespeicherter Zustand", "Saved state")
          : source === "previous"
          ? `${previous.count} / ${previous.total}`
          : `${summary.count} / ${evidence.games.length}`,
        period: isSaved
          ? ""
          : source === "previous"
          ? previous.period
          : evidence.games.length
          ? `${evidence.games
              .at(-1)
              .kickoff.slice(0, 10)} – ${evidence.games[0].kickoff.slice(
              0,
              10
            )}`
          : "",
        compact:
          goalFieldLabel(focusField, en) +
          " · " +
          t("Tore pro Spiel", "Goals per match"),
        details: [
          t(
            "Gespeicherte Gesamtwerte gewichten Heim und Auswärts gleich. Historische Werte sind Torsummen geteilt durch die Zahl der abgedeckten Spiele des gewählten Spielorts. Defensive zeigt Gegentore; weniger ist besser.",
            "Saved overall values weight home and away equally. Historical values are goal totals divided by covered matches at the selected venue. Defence shows goals conceded; fewer is better."
          ),
        ],
      };
    });
  if (supported) {
    const g = evidence.games.slice(0, recent);
    displayed.push({
      key: "recent",
      title: sourceNames.recent,
      value: metricFor(g, teamId, "should", question, focusField),
      coverage: `${historySummary(g, teamId, "should").count}/${g.length}`,
      period: g.length
        ? `${g.at(-1).kickoff.slice(0, 10)} – ${g[0].kickoff.slice(0, 10)}`
        : "",
      compact:
        evidence.games.length <= recent
          ? t(
              "Identisch mit der Saisonleistung; zusätzlich gewichtbar.",
              "Identical to season performance; may be weighted additionally."
            )
          : t(
              "Teilmenge der Saison. Der Vorschlag vergleicht Should Goals mit der Erwartung derselben Spiele.",
              "Subset of the season. Proposals compare Should Goals with expectations for these same matches."
            ),
      details: [
        t(
          "Die Tabellenzahl verwendet alle vorhandenen Should Goals dieses Zeitfensters. Für Vorschläge werden nur Spiele mit zusätzlich vorhandener Erwartung verwendet; deren Abdeckung steht unten.",
          "The table value uses all available Should Goals in this window. Proposals only use games with matching expectations; their coverage is shown below."
        ),
      ],
    });
  }
  const external = evidence.sources.find((s) => s.key === "external");
  const externalShared = evidence.games.filter(
    (g) =>
      external?.ids.includes(g.id) &&
      historySummary([g], teamId, "should").count
  );
  const externalSharedValue = metricFor(
    externalShared.map((g) => ({ ...g, should: g.external_xgoals })),
    teamId,
    "should",
    question,
    focusField
  );
  displayed.push({
    key: "external",
    title: sourceNames.external,
    value: external?.observed,
    coverage: `${external?.count || 0}/${external?.total || 0}`,
    period:
      external?.provider ||
      t("Keine passenden xGoals verfügbar", "No matching xGoals available"),
    compact:
      external?.count > 0
        ? t(
            "xGoals auf denselben Spielen wie die Modellerwartung; fehlende Anbieterwerte werden durch gemessene Should Goals ersetzt.",
            "xGoals on the same matches as model expectations; missing provider values use measured Should Goals."
          )
        : t("Noch keine Daten verfügbar", "No data available yet"),
    details: [
      t(
        "Anbieterwerte haben Vorrang. Wo sie fehlen, zählen gemessene Should Goals als gekennzeichneter Ersatz zusätzlich mit. Wunschgewicht bleibt erhalten; ohne passende Daten ist das wirksame Gewicht 0 %. Es wird kein Anbieter gebucht.",
        "Provider values take priority. Where missing, measured Should Goals additionally count as a labelled substitute. Desired weight is retained; without matching data the effective weight is 0%. No provider is subscribed."
      ),
      `${t("Should-Goals-Ersatz", "Should Goals substitutes")}: ${evidence.games.filter(g => external?.ids.includes(g.id) && g.external_xgoals?.kind === "should_goals_substitute").length}`,
      external?.count
        ? `${t(
            "Direkter Vergleich auf denselben Spielen",
            "Direct comparison on the same matches"
          )} (${externalShared.length}/${evidence.games.length}): xGoals ${fmt(
            externalSharedValue
          )} · Should Goals ${fmt(
            metricFor(externalShared, teamId, "should", question, focusField)
          )}`
        : "",
    ],
  });
  const tourPerformance = evidence.sources.find((s) => s.key === "should");
  const tourSteps = [
    ...displayed.map((s) => ({
      key: s.key,
      title: s.title,
      text: [s.compact, ...(s.details || [])].join(" "),
    })),
    {
      key: "conclusion",
      title: t("Zusammenhänge und Entscheidung", "Relationships and decision"),
      text:
        (supported && tourPerformance?.enabled
          ? t(
              `Gespeichert ${fmt(saved)}; Leistung ${fmt(
                tourPerformance.observed
              )}, Erwartung ${fmt(tourPerformance.expected)} auf ${
                tourPerformance.count
              } gemeinsamen Spielen. Voller Anpassungsweg bis ${fmt(
                tourPerformance.full
              )}. `,
              `Saved ${fmt(saved)}; performance ${fmt(
                tourPerformance.observed
              )}, expectation ${fmt(tourPerformance.expected)} on ${
                tourPerformance.count
              } shared matches. Full adjustment path to ${fmt(
                tourPerformance.full
              )}. `
            )
          : "") +
        t(
          "Leistung wird mit der jeweiligen historischen Erwartung verglichen. Unterschiedliche Vorzeichen können erklären, warum die beiden Modelle trotz derselben Leistung in entgegengesetzte Richtungen angepasst würden. Ergebnisse und Markt können davon abweichen. Gewichte und Anpassungsstärke sind Ihre Entscheidung; der Vorschlag wird erst auf Anforderung berechnet und niemals automatisch gespeichert.",
          "Performance is compared with each model’s historical expectation. Different signs can explain opposite adjustment directions despite identical performance. Results and market may differ. Weights and adjustment strength are your decision; proposals are requested explicitly and never saved automatically."
        ),
    },
    ...[
      "total_strength",
      "total_goals",
      "for_total",
      "against_total",
      "for_home",
      "against_home",
      "for_away",
      "against_away",
      "for_homefactor",
      "against_homefactor",
      ...(model === "result" ? ["draw_factor"] : []),
    ].map((field) => ({
      key: "individual-" + field,
      field,
      title: goalFieldLabel(field, en),
      text: t(
        "Gespeicherten Wert, Entwurf und passenden Ligavergleich nebeneinander prüfen. Offensive: höhere Werte bedeuten mehr Tore; Defensive: niedrigere Werte bedeuten weniger Gegentore. Die Änderungsregel steht am Eingabefeld.",
        "Compare saved value, draft and matching league reference side by side. Attack: higher means more goals; defence: lower means fewer goals conceded. The editing rule is shown beside the input."
      ),
    })),
    {
      key: "save",
      title: t("Prüfen und speichern", "Review and save"),
      text:
        tourMode === "explain"
          ? t(
              "Diese Erklärung verändert nichts. Zum Bearbeiten die Bearbeitungsrundreise wählen.",
              "This explanation changes nothing. Choose the editing tour to make changes."
            )
          : t(
              "Vorschlag oder Einzelwert erst prüfen, dann als Entwurf übernehmen. Unten Vorschau öffnen: sämtliche geschriebenen Werte vorher/nachher prüfen und bewusst Änderungen speichern. Verwerfen stellt den gespeicherten Zustand wieder her.",
              "Inspect the proposal or individual value, then apply as draft. Open Preview below: review every stored value before/after and explicitly Save changes. Discard restores saved settings."
            ),
    },
  ];
  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(
      () =>
        setTour((i) => {
          if (i >= tourSteps.length - 1) {
            setPlaying(false);
            return i;
          }
          return i + 1;
        }),
      12000
    );
    return () => clearInterval(timer);
  }, [playing, tourSteps.length]);
  const prepare = () => {
    if (!Number.isFinite(target) || target <= 0)
      throw Error(
        t(
          "Ein positiver Zielwert ist erforderlich.",
          "A positive target is required."
        )
      );
    // Change the offensive factor while explicitly holding the defensive factor.
    const value =
      question === "home" ? target * current.against_homefactor : target;
    if (question === "home" && value >= 2)
      throw Error(
        t(
          "Dieser Quotient erfordert einen offensiven Heimfaktor unter 2. Bitte beide Faktoren manuell prüfen.",
          "This ratio requires an attacking home factor below 2. Review both factors manually."
        )
      );
    return { team_id: Number(teamId), group: "summary", field, value };
  };
  const inspect = async () => {
    const generation = previewGeneration.current;
    setPending(true);
    setNotice("");
    try {
      const p = prepare();
      const impact =
        model === "result"
          ? {
              before: {
                ...current,
                rank: strengthRanking(draft).find(
                  (r) => r.id === Number(teamId)
                )?.rank,
              },
              after: strengthRanking({
                ...draft,
                [teamId]: editResult(current, p.field, p.value),
              }).find((r) => r.id === Number(teamId)),
            }
          : await onPreviewStat(p);
      if (generation === previewGeneration.current)
        setEffect({ ...impact, target });
    } catch (e) {
      setNotice(e.message);
    } finally {
      setPending(false);
    }
  };
  const apply = async () => {
    try {
      const p = prepare();
      const ok =
        model === "result"
          ? onApplyResult(teamId, p.field, p.value)
          : await onApplyStat(p);
      if (ok !== false) {
        setEffect(null);
        setNotice(
          t(
            "Als Entwurf übernommen. Erst „Änderungen speichern“ schreibt die Settings.",
            "Applied as a draft. Only “Save changes” writes settings."
          )
        );
      }
    } catch (e) {
      setNotice(e.message);
    }
  };
  const adjustWeight = (key, value) => {
    const active = keys.filter((k) => selectedSources.includes(k) || k === key);
    setWeights(moveWeight(weights, key, value, active));
  };
  const toggle = (key, enabled) => {
    setSelectedSources((s) =>
      enabled ? [...new Set([...s, key])] : s.filter((k) => k !== key)
    );
    if (enabled) {
      const active = keys.filter(
        (k) => selectedSources.includes(k) || k === key
      );
      setWeights(
        moveWeight(
          weights,
          key,
          active.length === 1 ? 100 : 100 / active.length,
          active
        )
      );
    } else {
      const rest = { ...weights, [key]: 0 };
      setWeights(normalizeWeights(rest, keys));
    }
  };
  const paired = evidence.sources.find((s) => s.key === "should");
  const alternative = useMemo(
    () =>
      proposalEvidence(
        data,
        teamId,
        model === "result" ? "statistics" : "result",
        question,
        recent,
        focusField
      ),
    [data, teamId, model, question, recent, focusField]
  );
  const alternativePerformance = alternative.sources.find(
    (s) => s.key === "should"
  );
  const actual = evidence.sources.find((s) => s.key === "real"),
    market = evidence.sources.find((s) => s.key === "market");
  return (
    <section className="tw-workbench" ref={workbenchRef}>
      {question !== "statistics" && (
        <div className="tw-editor">
          <div>
            <strong>
              {t("Aktuell gespeichert", "Currently saved")} ·{" "}
              {model === "result"
                ? t("Ergebnismodell", "Results model")
                : t("Statistikmodell", "Statistics model")}
            </strong>
            <div className="tw-saved">
              {fmt(saved)}{" "}
              <Button
                disabled={
                  busy ||
                  !Number.isFinite(saved) ||
                  (question === "draws" && model === "statistics")
                }
                onClick={() => {
                  setManual(fmt(saved));
                  setManualExact(saved);
                  setManualOverride(true);
                }}
              >
                {t("✎ Wert bearbeiten", "✎ Edit value")}
              </Button>
            </div>
            <small>
              {question === "home"
                ? t(
                    "Heimvorteil = offensiver / defensiver Heimfaktor",
                    "Home advantage = attacking / defensive home factor"
                  )
                : question === "draws"
                ? model === "statistics"
                  ? t(
                      "Liga-Remisfaktor · nur Referenz",
                      "League draw factor · reference only"
                    )
                  : t(
                      "Individueller Remisfaktor, kein Prozentwert",
                      "Individual draw factor, not a percentage"
                    )
                : focusField
                ? `${goalFieldLabel(focusField, en)} · ${t(
                    "Tore pro Spiel",
                    "Goals per match"
                  )}`
                : question === "strength"
                ? t("Spielstärke · Torquotient", "Strength · goal ratio")
                : t(
                    "Torniveau · Tore pro Spiel",
                    "Goal level · goals per match"
                  )}
            </small>
            {Number.isFinite(draftValue) && draftValue !== saved && (
              <p>
                {t("Entwurf", "Draft")}: <b>{fmt(draftValue)}</b>
              </p>
            )}
          </div>
          <div>
            <label>
              {t("Vorschlag / eigener Zielwert", "Proposal / your target")}
              <input
                aria-label={t("Zielwert", "Target value")}
                value={
                  manualOverride ? manual : proposal ? fmt(proposal.target) : ""
                }
                onBlur={() => {
                  if (manualOverride && Number.isFinite(target)) {
                    setManualExact(target);
                    setManual(fmt(target));
                  }
                }}
                placeholder="—"
                disabled={busy || (!supported && model !== "result")}
                onChange={(e) => {
                  setManual(e.target.value);
                  setManualExact(null);
                  setManualOverride(true);
                  setEffect(null);
                }}
              />
            </label>
            <small>
              {t(
                "Direkt änderbar. Noch kein gespeicherter Wert.",
                "Editable directly. Not a saved value yet."
              )}
            </small>
            <p>
              {t("Änderung", "Change")}:{" "}
              <b>
                {Number.isFinite(target) && Number.isFinite(saved)
                  ? Math.abs(target - saved) < 1e-12
                    ? t("Keine Änderung", "No change")
                    : Math.abs(target - saved) < 0.0005
                    ? t("Unter 0,001", "Below 0.001")
                    : `${target - saved > 0 ? "+" : ""}${fmt(target - saved)}`
                  : "—"}
              </b>
            </p>
          </div>
        </div>
      )}
      {question === "home" && (
        <div className="tw-factors">
          {["for_homefactor", "against_homefactor"].map((f) => (
            <Factor
              key={model + f}
              label={
                f === "for_homefactor"
                  ? t("Offensiver Heimfaktor", "Attacking home factor")
                  : t("Defensiver Heimfaktor", "Defensive home factor")
              }
              value={current?.[f]}
              busy={busy}
              t={t}
              onApply={(v) =>
                model === "result"
                  ? onApplyResult(teamId, f, v)
                  : onApplyStat({
                      team_id: Number(teamId),
                      group: "summary",
                      field: f,
                      value: v,
                    })
              }
            />
          ))}
          <small>
            {t(
              "Quotient-Ziel hält den defensiven Faktor konstant; Gesamtstärke und Torniveau bleiben erhalten. Beide Faktoren sind einzeln änderbar.",
              "Ratio target holds the defensive factor constant; overall strength and goal level remain unchanged. Both factors can be edited individually."
            )}
          </small>
        </div>
      )}
      {question === "draws" && (
        <Alert severity="info">
          {t(
            "Remisanteil und Remisfaktor sind verschiedene Größen. Eine automatische Umrechnung ist noch nicht kalibriert. Der individuelle Faktor ist im Ergebnismodell manuell bearbeitbar; das Statistikmodell verwendet derzeit den Liga-Faktor. Es wird kein zweiter Teamfaktor erfunden.",
            "Draw rate and draw factor are different quantities. Automatic conversion is not calibrated. The individual factor can be edited manually in the results model; the statistics model currently uses the league factor. No second team factor is invented."
          )}
        </Alert>
      )}
      <TeamIndividualEditor
        {...{
          data,
          teamId,
          model,
          draft,
          ranking,
          en,
          busy,
          onApplyResult,
          onApplyStat,
          onPreviewStat,
        }}
        busy={busy || (tour >= 0 && tourMode === "explain")}
        open={tour >= 0 && !!tourSteps[tour]?.field}
        onFocusField={onFocusField}
        focusField={tour >= 0 ? tourSteps[tour]?.field : focusField}
      />
      <div className="tw-tour-controls">
        <Button
          onClick={() => {
            setTourMode("explain");
            setTour(0);
            setPlaying(false);
          }}
        >
          {t("Geführte Rundreise", "Guided tour")} ·{" "}
          {t("Erklärung", "Explanation")}
        </Button>
        <Button
          onClick={() => {
            setTourMode("edit");
            setTour(0);
            setPlaying(false);
          }}
        >
          {t("Rundreise · Bearbeitung", "Tour · editing")}
        </Button>
        {tour >= 0 && (
          <>
            <Button onClick={() => setPlaying((p) => !p)}>
              {playing
                ? t("Pause", "Pause")
                : t("Automatisch vorführen", "Play automatically")}
            </Button>
            <Button disabled={tour === 0} onClick={() => setTour((i) => i - 1)}>
              {t("Zurück", "Back")}
            </Button>
            <Button
              disabled={tour >= tourSteps.length - 1}
              onClick={() => setTour((i) => i + 1)}
            >
              {t("Weiter", "Next")}
            </Button>
            <Button
              onClick={() => {
                setTour(-1);
                workbenchRef.current
                  ?.querySelector(".tw-tour-controls")
                  ?.scrollIntoView?.({ block: "center" });
                setPlaying(false);
              }}
            >
              {t("Rundreise beenden", "End tour")}
            </Button>
          </>
        )}
      </div>
      {tour >= 0 && (
        <aside className="tw-tour" role="status">
          <strong>{tourSteps[tour]?.title}</strong>
          <p>{tourSteps[tour]?.text}</p>
        </aside>
      )}
      <div className="ta-scroll">
        <table className="tw-comparison">
          <thead>
            <tr>
              <th>{t("Quelle", "Source")}</th>
              <th>
                {focusField
                  ? focusField.startsWith("against")
                    ? t("Gegentore / Spiel", "Conceded / match")
                    : t("Tore / Spiel", "Goals / match")
                  : t("Wert", "Value")}
              </th>
              {supported && <th>{t("Δ zu gespeichert", "Δ from saved")}</th>}
              <th>{t("Spiele / Zeitraum", "Matches / period")}</th>
              <th>{t("Herkunft & Erklärung", "Origin & explanation")}</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((s) => (
              <tr
                key={s.key}
                className={
                  tour >= 0 && tourSteps[tour]?.key === s.key
                    ? "tw-highlight"
                    : ""
                }
              >
                <th>{s.title}</th>
                <td>
                  <b>{fmt(s.value)}</b>
                </td>
                {supported && (
                  <td>
                    <strong
                      className={
                        s.value > saved
                          ? "tw-positive"
                          : s.value < saved
                          ? "tw-negative"
                          : ""
                      }
                    >
                      {Number.isFinite(s.value) && Number.isFinite(saved)
                        ? Math.abs(s.value - saved) < 0.0005
                          ? fmt(0)
                          : `${s.value > saved ? "+" : ""}${fmt(
                              s.value - saved
                            )}`
                        : "—"}
                    </strong>
                  </td>
                )}
                <td>
                  <small>
                    {s.coverage || t("Gespeicherte Settings", "Saved settings")}
                    <br />
                    {s.period}
                  </small>
                </td>
                <td>
                  <Button
                    aria-label={`${t("Information", "Information")}: ${
                      s.title
                    }`}
                    onClick={() => setInfo(s)}
                  >
                    ⓘ
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {supported && (
        <>
          <section className="tw-interpretation">
            <h3>
              {t(
                "Leistung im Verhältnis zur Erwartung",
                "Performance relative to expectation"
              )}
            </h3>
            {evidence.sources
              .filter((s) => ["should", "recent"].includes(s.key))
              .map((s) => (
                <p key={s.key}>
                  <b>{sourceNames[s.key]}</b>
                  {focusField?.startsWith("against")
                    ? t(" · vermiedene Gegentore", " · goals conceded avoided")
                    : ""}
                  :{" "}
                  {fmt(
                    focusField?.startsWith("against") ? s.expected : s.observed
                  )}{" "}
                  −{" "}
                  {fmt(
                    focusField?.startsWith("against") ? s.observed : s.expected
                  )}{" "}
                  ={" "}
                  <strong
                    className={
                      focusField?.startsWith("against")
                        ? s.observed <= s.expected
                          ? "tw-positive"
                          : "tw-negative"
                        : s.observed >= s.expected
                        ? "tw-positive"
                        : "tw-negative"
                    }
                  >
                    {Number.isFinite(s.observed) && Number.isFinite(s.expected)
                      ? `${
                          (s.observed - s.expected) *
                            (focusField?.startsWith("against") ? -1 : 1) >=
                          0
                            ? "+"
                            : ""
                        }${fmt(
                          (s.observed - s.expected) *
                            (focusField?.startsWith("against") ? -1 : 1)
                        )}`
                      : "—"}
                  </strong>{" "}
                  · {s.count}/{s.total}{" "}
                  {t("gemeinsame Spiele", "shared matches")}.{" "}
                  {s.identical &&
                    t(
                      "Identisch mit der Saison; zusätzliche Gewichtung betont diese Leistung.",
                      "Identical to season; additional weighting emphasizes this performance."
                    )}
                </p>
              ))}
            {paired?.enabled && (
              <p>
                {t(
                  "Zielwert dieser Leistungsquelle",
                  "Target from this performance source"
                )}{" "}
                <b>{fmt(paired.full)}</b>.{" "}
                {t(
                  "Die historische Abweichung erklärt die Leistung. Sie wird nicht nochmals auf den gespeicherten Wert addiert.",
                  "The historical deviation explains performance. It is not added again to the saved value."
                )}
              </p>
            )}
            {paired?.enabled && alternativePerformance?.enabled && (
              <p>
                {t(
                  "Beide Modelle im Vergleich: Leistungsabweichung gegenüber dem gewählten Modell",
                  "Both models compared: performance deviation from the selected model"
                )}{" "}
                <b>
                  {paired.observed - paired.expected > 0 ? "+" : ""}
                  {fmt(paired.observed - paired.expected)}
                </b>
                ; {t("gegenüber dem anderen Modell", "from the other model")}{" "}
                <b>
                  {alternativePerformance.observed -
                    alternativePerformance.expected >
                  0
                    ? "+"
                    : ""}
                  {fmt(
                    alternativePerformance.observed -
                      alternativePerformance.expected
                  )}
                </b>
                .{" "}
                {(paired.observed - paired.expected) *
                  (alternativePerformance.observed -
                    alternativePerformance.expected) <
                0
                  ? t(
                      "Die gemessene Leistung spricht somit für entgegengesetzte Korrekturrichtungen der beiden gespeicherten Modelle.",
                      "Measured performance therefore suggests opposite correction directions for the two saved models."
                    )
                  : t(
                      "Beide Modelle zeigen dieselbe Richtung der Leistungsabweichung.",
                      "Both models show the same direction of performance deviation."
                    )}
              </p>
            )}
            {question === "strength" &&
              !focusField &&
              Number.isFinite(actual?.observed) &&
              Number.isFinite(paired?.observed) &&
              actual.ids.join() === paired.ids.join() && (
                <p>
                  {t("Ergebnisbilanz", "Actual record")} {fmt(actual.observed)}{" "}
                  · {t("gemessene Leistung", "measured performance")}{" "}
                  {fmt(paired.observed)} · {t("Markt", "market")}{" "}
                  {fmt(market?.observed)}.{" "}
                  {actual.observed < paired.observed
                    ? t(
                        "Die Ergebnisse fallen schwächer aus als die gemessene Leistung. Das ist ein Hinweis auf Ergebnispech; eine mögliche Unterbewertung durch den Markt muss zusätzlich an den Quoten geprüft werden.",
                        "Results are weaker than measured performance. This suggests outcome misfortune; possible market undervaluation still needs to be checked against odds."
                      )
                    : t(
                        "Die Ergebnisse fallen mindestens so hoch aus wie die gemessene Leistung. Eine mögliche Ergebnisüberbewertung durch den Markt ist zu prüfen.",
                        "Results are at least as high as measured performance. Possible market overvaluation of results should be examined."
                      )}
                </p>
              )}
            <p>
              {t(
                "Ergebnisse und Markt werden jeweils gegen die Erwartung desselben Modells auf denselben Spielen eingeordnet. Der Markt ist keine Wahrheit. Das andere Modell bleibt eine getrennte Einschätzung.",
                "Results and market are each compared against this model’s expectation on the same matches. The market is not ground truth. The other model remains an independent assessment."
              )}
            </p>
          </section>
          <details className="tw-weights" open>
            <summary>{t("Persönliche Gewichtung", "Personal weights")}</summary>
            <TeamWeightSetup
              sources={evidence.sources}
              names={sourceNames}
              en={en}
              onApply={(next, strength, kind) => {
                setWeights(next);
                setSelectedSources(
                  Object.keys(next).filter((k) => next[k] > 0)
                );
                if (strength !== null) setAdapt(strength);
                setNotice(
                  kind === "random"
                    ? t(
                        "Zufallsgewichtung übernommen. Du kannst sie ändern, speichern oder erneut würfeln.",
                        "Random weights applied. Edit, save or roll again."
                      )
                    : t(
                        "Deine Einschätzung bestimmt jetzt die Gewichtung. Noch keine Settings geändert.",
                        "Your preferences now determine the weights. No settings changed."
                      )
                );
              }}
            />
            <p>
              {t(
                question === "home"
                  ? "Je Quelle: der beobachtete Heimquotient ist der Zielwert. Die Erwartung dient der Einordnung; Δ zeigt Zielwert minus gespeichert."
                  : "Je Quelle siehst du ihren Zielwert und seinen Abstand zum aktuell gespeicherten Wert. Historische Erwartungen dienen der Einordnung und werden nicht erneut aufaddiert.",
                question === "home"
                  ? "Each source uses its observed home ratio as its target. Historical expectations provide context; Δ is target minus saved."
                  : "Each source shows its target and difference from the saved value. Historical expectations provide context and are not added again."
              )}
            </p>
            {evidence.sources.map((s) => (
              <div className="tw-weight" key={s.key}>
                <label>
                  <input
                    type="checkbox"
                    checked={selectedSources.includes(s.key)}
                    disabled={!s.enabled && !s.desiredSelectable}
                    onChange={(e) => toggle(s.key, e.target.checked)}
                  />
                  {sourceNames[s.key]}
                </label>
                <input
                  aria-label={`${t("Gewicht", "Weight")} ${sourceNames[s.key]}`}
                  type="range"
                  min="0"
                  max="100"
                  step="0.01"
                  value={normalized[s.key] || 0}
                  disabled={
                    (!s.enabled && !s.desiredSelectable) ||
                    !selectedSources.includes(s.key)
                  }
                  onChange={(e) => adjustWeight(s.key, Number(e.target.value))}
                />
                <output>
                  {(normalized[s.key] || 0).toFixed(1)} %{" "}
                  {s.key === "external" ? t("Wunsch", "desired") : ""}
                </output>
                <small>
                  {s.enabled && (
                    <span className="tw-source-target">
                      <b>
                        {t("Zielwert", "Target")}: {fmt(s.full)}
                      </b>
                      {" · Δ "}
                      {t("zu gespeichert", "from saved")}{" "}
                      <strong
                        className={
                          settingDirection(
                            focusField ||
                              (question === "strength"
                                ? "total_strength"
                                : "total_goals"),
                            saved,
                            s.full
                          ) > 0
                            ? "tw-positive"
                            : settingDirection(
                                focusField ||
                                  (question === "strength"
                                    ? "total_strength"
                                    : "total_goals"),
                                saved,
                                s.full
                              ) < 0
                            ? "tw-negative"
                            : ""
                        }
                      >
                        {Math.abs(s.full - saved) < 0.0005
                          ? fmt(0)
                          : `${s.full > saved ? "+" : ""}${fmt(
                              s.full - saved
                            )}`}
                      </strong>
                      {Number.isFinite(s.observed) &&
                        Number.isFinite(s.expected) && (
                          <span className="tw-source-formula">
                            {t("Quelle", "Source")} {fmt(s.observed)}{" "}
                            {question === "home" ? "/" : "−"}{" "}
                            {t("Erwartung", "expectation")} {fmt(s.expected)}
                            {question === "home"
                              ? t(
                                  " · historischer Vergleich",
                                  " · historical comparison"
                                )
                              : ` = ${s.observed >= s.expected ? "+" : ""}${fmt(
                                  s.observed - s.expected
                                )}`}
                          </span>
                        )}
                      {s.pointFactor < 1 && (
                        <span>
                          {t(
                            " · Bessere Punktausbeute mildert die negative Torbilanzkorrektur.",
                            " · Better points return softens the adverse goal-record correction."
                          )}
                        </span>
                      )}
                    </span>
                  )}
                  {s.enabled
                    ? s.count !== null
                      ? `${s.count}/${s.total} · `
                      : ""
                    : s.key === "external"
                    ? t(
                        "— · Noch keine Daten verfügbar · wirksam 0 %",
                        "— · No data available yet · effective 0%"
                      )
                    : s.reason === "PREVIOUS_LEAGUE_DIFFERENT"
                    ? t("Andere Liga in der Vorsaison · nur Vergleich · Gewicht 0 %", "Different prior league · comparison only · weight 0%")
                    : t("Keine vergleichbare Grundlage", "No comparable basis")}
                  {s.identical &&
                    ` · ${t(
                      "Gleiche Spiele wie Saison; zusätzlich gewichtbar",
                      "Same matches as season; may be weighted additionally"
                    )}`}
                  {s.key === "previous" &&
                    s.enabled &&
                    ` · ${s.period} · ${t(
                      "Reale Vorsaisonbilanz, ohne Erwartungsbereinigung",
                      "Actual prior-season record, without expectation adjustment"
                    )}`}
                  {!!normalized[s.key] &&
                    ` · ${t("Wirksames Gewicht", "Effective weight")}: ${(
                      effective[s.key] || 0
                    ).toFixed(1)} %`}
                </small>
              </div>
            ))}
            <p>
              <b>
                {t("Summe Wunschgewichte", "Total desired weights")}:{" "}
                {configured
                  ? "100 %"
                  : t("Noch kein Profil gewählt", "No profile selected")}
              </b>
            </p>
            <label>
              {t("Anpassungsstärke", "Adjustment strength")}: {adapt} %{" "}
              <input
                aria-label={t("Anpassungsstärke", "Adjustment strength")}
                type="range"
                min="0"
                max="100"
                step="1"
                value={adapt}
                onChange={(e) => setAdapt(Number(e.target.value))}
              />
            </label>
            <p>
              <small>
                {t(
                  "25 % bedeutet ein Viertel des gewichteten Anpassungswegs. Gewichte entscheiden Sie selbst. Vorsaisonkurve: 1 / (1 + exp((Spiele − 6,5) / 2,5)); aktuell",
                  "25% means one quarter of the weighted adjustment path. You choose the weights. Prior-season curve: 1 / (1 + exp((matches − 6.5) / 2.5)); currently"
                )}{" "}
                {(evidence.decay * 100).toFixed(1)} %.{" "}
                {t(
                  "Das Vorsaisongewicht wird mit diesem Faktor multipliziert; anschließend werden alle wirksamen Gewichte auf 100 % normiert. Vorsaisonergebnisse sind auch ohne damalige Erwartungen wählbar.",
                  "The prior-season weight is multiplied by this factor; all effective weights are then normalized to 100%. Prior-season results can be selected without historical expectations."
                )}
              </small>
            </p>
            <Button
              onClick={() => {
                try {
                  localStorage.setItem(
                    profileKey,
                    JSON.stringify({ weights, adapt, selectedSources })
                  );
                  localStorage.setItem(
                    globalProfileKey,
                    JSON.stringify({ weights, adapt, selectedSources })
                  );
                  setNotice(
                    t(
                      "Persönliches Profil auf diesem Gerät gespeichert; für alle Teams, Ligen und Fragestellungen dieses Modells verfügbar.",
                      "Personal profile saved on this device; available across teams, leagues and questions for this model."
                    )
                  );
                } catch {
                  setNotice(
                    t(
                      "Profil konnte nicht gespeichert werden.",
                      "Could not save profile."
                    )
                  );
                }
              }}
              disabled={!configured}
            >
              {t(
                "Profil auf diesem Gerät speichern",
                "Save profile on this device"
              )}
            </Button>
            <Button
              disabled={!configured}
              onClick={() => {
                try {
                  localStorage.setItem(
                    teamProfileKey,
                    JSON.stringify({ weights, adapt, selectedSources })
                  );
                  setNotice(
                    t(
                      "Abweichendes Profil für dieses Team auf diesem Gerät gespeichert.",
                      "Team-specific profile saved on this device."
                    )
                  );
                } catch {
                  setNotice(
                    t(
                      "Profil konnte nicht gespeichert werden.",
                      "Could not save profile."
                    )
                  );
                }
              }}
            >
              {t("Teamprofil speichern", "Save team profile")}
            </Button>
            <Button
              onClick={() => {
                try {
                  const p = JSON.parse(
                    localStorage.getItem(globalProfileKey) ||
                      localStorage.getItem(profileKey)
                  );
                  if (!p) throw Error();
                  setWeights(p.weights);
                  setSelectedSources(
                    p.selectedSources ||
                      Object.keys(p.weights).filter((k) => p.weights[k] > 0)
                  );
                  setAdapt(p.adapt);
                  setNotice(
                    t("Gespeichertes Profil geladen.", "Saved profile loaded.")
                  );
                } catch {
                  setNotice(
                    t(
                      "Kein gespeichertes Profil vorhanden.",
                      "No saved profile available."
                    )
                  );
                }
              }}
            >
              {t("Profil laden", "Load profile")}
            </Button>
          </details>
          <Button
            variant="contained"
            disabled={!configured || busy}
            onClick={() => {
              setRequested(true);
              setManualOverride(false);
              setEffect(null);
            }}
          >
            {t("Vorschlag einholen", "Request proposal")}
          </Button>
          {proposal && (
            <p>
              {t("Gewichteter voller Zielwert", "Weighted full target")}{" "}
              {fmt(proposal.full)} → {adapt} % → <b>{fmt(proposal.target)}</b>.{" "}
              {t(
                "Änderungen der Gewichte aktualisieren diesen Vorschlag; übernehmen und speichern bleiben getrennte Aktionen.",
                "Changing weights updates this proposal; applying and saving remain separate actions."
              )}
            </p>
          )}
        </>
      )}
      {question !== "statistics" && (
        <div className="tw-actions">
          <Button
            disabled={
              busy || pending || !Number.isFinite(target) || target <= 0
            }
            onClick={inspect}
          >
            {t("Auswirkung prüfen", "Inspect effect")}
          </Button>
          <Button
            disabled={busy || pending || !effect || effect.target !== target}
            onClick={apply}
          >
            {t("Als Entwurf übernehmen", "Apply as draft")}
          </Button>
          <Button
            onClick={() => {
              setRequested(false);
              setManualOverride(false);
              setManual("");
              setEffect(null);
            }}
          >
            {t("Vorschlag verwerfen", "Discard proposal")}
          </Button>
        </div>
      )}
      {effect && (
        <p role="status">
          {t(
            "Bestehender Entwurf → mit Zielwert",
            "Existing draft → with target"
          )}
          : {t("Spielstärke", "Strength")} {fmt(effect.before?.total_strength)}{" "}
          → {fmt(effect.after?.total_strength)} · {t("Torniveau", "Goal level")}{" "}
          {fmt(effect.before?.total_goals)} → {fmt(effect.after?.total_goals)} ·{" "}
          {t("Heimvorteil", "Home advantage")}{" "}
          {fmt(homeAdvantage(effect.before))} →{" "}
          {fmt(homeAdvantage(effect.after))} · {t("Modellrang", "Model rank")}{" "}
          {effect.before?.rank ?? "—"} → {effect.after?.rank ?? "—"}.{" "}
          {t("Noch nicht übernommen.", "Not applied yet.")}
        </p>
      )}
      {effect && (
        <TeamValueChanges
          names={(id) => data.result_teams?.[id]?.team_name || id}
          registry={data.state?.registry}
          en={en}
          rows={
            model === "result"
              ? changedFields(data.result_teams?.[teamId], effect.after)
                  .filter(
                    (c) => !["rank", "team_id", "total_goals"].includes(c.field)
                  )
                  .map((c) => ({ id: teamId, model, group: "", ...c }))
              : settingChanges(data, {}, effect.state)
          }
        />
      )}
      {notice && <Alert severity="info">{notice}</Alert>}
      <details className="tw-matches">
        <summary>
          {t(`Letzte ${recent} Spiele`, `Last ${recent} matches`)} ·{" "}
          {Math.min(recent, evidence.games.length)}/{recent}
        </summary>
        <TeamMatches
          games={evidence.games.slice(0, recent)}
          teamId={teamId}
          en={en}
          model={model}
        />
      </details>
      <Dialog
        open={!!info}
        onClose={() => setInfo(null)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1800 }}
        PaperProps={{ className: `team-analysis ta-${mode}` }}
      >
        <DialogTitle className="tw-dialog-title">
          {info?.title}
          <Button onClick={() => setInfo(null)}>
            {t("Schließen", "Close")}
          </Button>
        </DialogTitle>
        <DialogContent>
          <p>{info?.compact}</p>
          {info?.details?.map((v, i) => (
            <p key={i}>{v}</p>
          ))}
          <small>
            {info?.coverage} · {info?.period}
          </small>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfo(null)}>
            {t("Schließen", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}
function Factor({ label, value, busy, t, onApply }) {
  const [v, setV] = useState("");
  useEffect(() => setV(Number.isFinite(value) ? String(value) : ""), [value]);
  return (
    <label>
      {label}
      <input
        type="number"
        step="0.001"
        min="0.001"
        max="1.999"
        value={v}
        onChange={(e) => setV(e.target.value)}
        disabled={busy}
      />
      <Button
        disabled={busy || !v || !(+v > 0 && +v < 2)}
        onClick={() => onApply(+v)}
      >
        {t("Als Entwurf", "As draft")}
      </Button>
    </label>
  );
}
